import type {
    AxisLabelsFormatterCallbackFunction,
    AxisLabelsFormatterContextObject,
    NumberFormatterCallbackFunction,
    Point,
    TooltipFormatterCallbackFunction,
} from 'highcharts';
import moment from 'moment';
import { useCallback, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { MAX_EVENTS_SHOW_IN_POPOVER } from '../../components/projection/cashEventPane/CashEventCard';
import { CashEventTooltipContent } from '../../components/projection/cashEventPane/CashEventTooltipContent';
import { MONTH_12_VALUE } from '../../config/constants';
import type { ITooltipContent, ITooltipSeries } from '../../interfaces/chart';
import type { RoadmapEvent } from '../../interfaces/event';
import type { ExtendedSeriesOptionType } from '../../interfaces/projection';
import { calculateAge } from '../../utils/date-utils';
import { formatNumberToCompactNotation } from '../../utils/renderUtils';
import { useYearEventMap } from './useYearEventMap';

interface IProps {
    chartType: 'CASH_FLOW' | 'MONTE_CARLO_PROJECTION';
    clientBirthdate?: string;
    series: ExtendedSeriesOptionType[];
    allEvents?: RoadmapEvent[];
    projectionPeriodYears?: number;
    isClientOutput?: boolean;
    isProjectionOutput?: boolean;
    hasWarning?: boolean;
}

export const useChartFormatter = ({
    hasWarning,
    chartType,
    clientBirthdate,
    series,
    allEvents = [],
    projectionPeriodYears = 0,
    isClientOutput,
    isProjectionOutput,
}: IProps) => {
    const { t } = useTranslation();
    const getYear = useCallback((unixEpoch: number) => moment(unixEpoch).year(), []);
    const getMonth = useCallback((unixEpoch: number) => moment(unixEpoch).month(), []);
    const xAxisYPosition = useRef(undefined);

    const { getEventsMap } = useYearEventMap({ allEvents, projectionPeriodYears, xAxisYPosition });

    const yearEventMap = getEventsMap();
    const getEvents = (pos: string | number) => {
        const year = getYear(Number(pos));
        if (getMonth(Number(pos)) === MONTH_12_VALUE) {
            const nextYear = year + 1;

            return nextYear in yearEventMap ? yearEventMap[year + 1].eventsAtCoordinate! : [];
        }

        return year in yearEventMap ? yearEventMap[year].eventsAtCoordinate! : [];
    };

    const renderCashEventTooltipContent = (events: RoadmapEvent[], tooltipContent: ITooltipContent) => {
        return ReactDOMServer.renderToString(
            <CashEventTooltipContent maxEvents={MAX_EVENTS_SHOW_IN_POPOVER} events={events} handleClose={() => {}} tooltipContent={tooltipContent} />,
        );
    };

    const mapToSeries = (contextObjects: Point[]): ITooltipSeries[] =>
        contextObjects.map((formatterCtx: Point) => ({
            name: formatterCtx.series.name,
            value: formatterCtx.y ?? 0,
            color: formatterCtx.color as string,
            type: formatterCtx.series.type,
        }));

    // eslint-disable-next-line react-hooks/unsupported-syntax
    const tooltipFormatter: TooltipFormatterCallbackFunction = function (this: Point) {
        let tooltipContent: ITooltipContent = {
            time: moment(this.x),
            position: { x: Number(this.x), y: Number(this.y) },
            series: mapToSeries(this.points!),
        };
        if (chartType === 'CASH_FLOW') {
            tooltipContent = { ...tooltipContent, title: t('cashflowAsOfYear', { year: tooltipContent.time.year() }) };

            return `${renderCashEventTooltipContent(getEvents(this.x!), tooltipContent)}`;
        } else if (chartType === 'MONTE_CARLO_PROJECTION') {
            tooltipContent = { ...tooltipContent, title: t('expectedAssets', { time: moment(this.x).format('MM.YYYY') }) };

            return `${renderCashEventTooltipContent(getEvents(this.x!), tooltipContent)}`;
        } else {
            return null;
        }
    };

    const xAxisLabelsFormatter: AxisLabelsFormatterCallbackFunction = useCallback(
        (formatterCtx: AxisLabelsFormatterContextObject) => {
            const label = formatterCtx.axis.defaultLabelFormatter.call(formatterCtx);
            if (clientBirthdate) {
                const age = calculateAge(moment(clientBirthdate), moment(formatterCtx.value));
                if (isClientOutput) {
                    if (isProjectionOutput) {
                        return `<span class="label-client-age-projection-output">${age}</span><br/><span class="label-client-year-projection-output">${moment(
                            formatterCtx.value,
                        ).year()}</span>`;
                    }

                    return `<span><p>&nbsp;&nbsp;&nbsp;</p><p class='label-client-age-output'>${age}</p> <br/>  <p class='label-client-year-output'>${moment(
                        formatterCtx.value,
                    ).year()}</p> </span>`;
                }

                return `<span><p>&nbsp;&nbsp;</p><p class='label-client-age'>${age}</p> <br/><br/>  <p class='label-client-year'>${moment(
                    formatterCtx.value,
                ).year()}</p> </span>`;
            }

            return label;
        },
        [clientBirthdate, isClientOutput, isProjectionOutput],
    );

    const yAxisLabelsFormatter: AxisLabelsFormatterCallbackFunction = useCallback((formatterCtx: AxisLabelsFormatterContextObject) => {
        const chart = formatterCtx.chart;
        const numericSymbols = chart.options.lang?.numericSymbols ?? [];
        const tickValue = Number(formatterCtx.value);
        const numberFormatterFn = chart.numberFormatter;

        return formatTickValueToCompactNotation(tickValue, numericSymbols, numberFormatterFn);
    }, []);

    const formatTickValueToCompactNotation = (tickValue: number, numericSymbols: string[], numberFormatterFn: NumberFormatterCallbackFunction) => {
        let numericSymbolsLength = numericSymbols && numericSymbols.length;
        while (numericSymbolsLength--) {
            const unitDivisor = Math.pow(10 ** 3, numericSymbolsLength + 1);
            if (tickValue % unitDivisor !== tickValue) {
                return numberFormatterFn(tickValue / unitDivisor, -1) + numericSymbols[numericSymbolsLength];
            }
        }

        if (Math.abs(tickValue) >= 10 ** 4) {
            return numberFormatterFn(tickValue, -1); // add thousands separators
        } else {
            return numberFormatterFn(tickValue, -1, undefined, ''); // small numbers
        }
    };

    const clientOutputMonteCarloLegendFormatter = useCallback(
        (point: Point) => {
            const getTimeWeightedReturn = (twr: number) => (hasWarning || !twr ? '' : ` <br> ${twr.toFixed(2)}% p.a`);
            try {
                // @ts-expect-error Problems with missing Highcharts types
                const { amountAtProjectEnd, twr } = series.find((serie) => serie.name === point.name);

                return `${point?.name} <br>  ${formatNumberToCompactNotation(amountAtProjectEnd)} ${getTimeWeightedReturn(twr)}`;
            } catch {
                return `${point?.name}`;
            }
        },
        [series, hasWarning],
    );

    return {
        tooltipFormatter,
        xAxisLabelsFormatter,
        clientOutputLegendFormatter: clientOutputMonteCarloLegendFormatter,
        yAxisLabelsFormatter,
    };
};
